import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import useToggle from 'dpl/shared/hooks/useToggle';

export default function TruncatedText({
  text,
  maxLines,
  lineHeight,
  className,
  moreText,
  lessText
}) {
  const rootRef = useRef(null);
  const textRef = useRef(null);
  const [isExpanded, toggleIsExpanded] = useToggle(false);
  const [isTruncated, setIsTruncated] = useState(false);

  const lineClampStyle = {
    lineHeight,
    ...(!isExpanded && {
      display: '-webkit-box',
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      WebkitLineClamp: isExpanded ? 'none' : maxLines
    })
  };

  useEffect(() => {
    function checkTruncation() {
      if (textRef.current) {
        const element = textRef.current;

        const computedFontSize = parseFloat(
          window.getComputedStyle(element).fontSize
        );

        const computedLineHeight = computedFontSize * lineHeight;
        const maxHeight = computedLineHeight * maxLines;
        const tempElement = element.cloneNode(true);

        tempElement.style.position = 'absolute';
        tempElement.style.visibility = 'hidden';
        tempElement.style.height = 'auto';
        tempElement.style.webkitLineClamp = 'none';
        tempElement.style.overflow = 'visible';
        tempElement.style.display = 'block';
        tempElement.style.lineHeight = lineHeight.toString();

        rootRef.current.appendChild(tempElement);
        setIsTruncated(tempElement.scrollHeight > maxHeight);
        rootRef.current.removeChild(tempElement);
      }
    }

    checkTruncation();

    window.addEventListener('resize', checkTruncation);

    return () => window.removeEventListener('resize', checkTruncation);
  }, [text, maxLines]);

  return (
    <div
      ref={rootRef}
      className={classnames('TruncatedText relative', className)}
    >
      <div ref={textRef} style={lineClampStyle}>
        {text}
      </div>
      {isTruncated && (
        <button
          type="button"
          className="fw-medium stone-700 underline mt2"
          onClick={toggleIsExpanded}
        >
          {isExpanded ? lessText : moreText}
        </button>
      )}
    </div>
  );
}

TruncatedText.propTypes = {
  text: PropTypes.string.isRequired,
  maxLines: PropTypes.number,
  lineHeight: PropTypes.number,
  className: PropTypes.string,
  moreText: PropTypes.string,
  lessText: PropTypes.string
};

TruncatedText.defaultProps = {
  className: null,
  maxLines: 3,
  lineHeight: 1.5,
  moreText: 'Read more',
  lessText: 'Show less'
};
